import { Component, type ErrorInfo, type ReactNode } from 'react';
import { handleReactError } from '@/utils/globalErrorHandler';

interface Props {
  children: ReactNode;
  /**
   * What to render instead of the crashed subtree. Given the error and a reset
   * callback so the fallback can offer a retry.
   */
  fallback?: (error: Error, reset: () => void) => ReactNode;
  /**
   * Changing this resets the boundary. Route-level boundaries pass the pathname,
   * so navigating away from a broken page clears the error instead of leaving
   * the fallback stuck on screen.
   */
  resetKey?: string;
}

interface State {
  error: Error | null;
}

/**
 * Stops one broken component from blanking the whole site.
 *
 * There was no boundary anywhere: a render error unmounted the entire tree and
 * left a white page, on the marketing site and in the admin panel alike. The
 * existing utils/globalErrorHandler had a `handleReactError` written for exactly
 * this and nothing ever called it.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    handleReactError(error, errorInfo);
  }

  componentDidUpdate(prevProps: Props): void {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  private reset = () => this.setState({ error: null });

  render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    if (this.props.fallback) return this.props.fallback(error, this.reset);

    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 px-6 text-center">
        <h1 className="text-2xl font-semibold">Something went wrong</h1>
        <p className="max-w-md text-sm text-soft">
          This part of the page failed to load. The rest of the site still works.
        </p>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={this.reset}
            className="rounded-lg bg-blue px-4 py-2 text-sm font-semibold text-navy"
          >
            Try again
          </button>
          <a
            href="/"
            className="rounded-lg border border-hair px-4 py-2 text-sm font-semibold"
          >
            Go to the homepage
          </a>
        </div>
      </div>
    );
  }
}
